/* =============================================================================
 * NoteReplyThread: replies under a coach note (APS-253)
 * -----------------------------------------------------------------------------
 * Used on the family dashboard (CoachNotesSection) and on the staff player
 * page (StaffAthleteProfile). The parent renders it only when the notes list
 * came back with `replies_enabled: true`.
 * ========================================================================== */
import React, { useEffect, useRef, useState } from 'react';
import { AlertCircle, CornerDownRight } from 'react-feather';
import { Button, MessageBubble, TextArea } from './ui';
import ConfirmDialog from './ConfirmDialog';
import { font, space } from '../styles/tokens';
import type { NoteReply } from '../services/staffAPI';

export const DEFAULT_REPLY_MAX_LENGTH = 2000;

interface NoteReplyThreadProps {
  noteId: string;
  replies: NoteReply[];
  /** False hides the Reply button, e.g. the player has left the note's team. */
  canReply: boolean;
  maxLength?: number;
  send: (noteId: string, body: string) => Promise<{ reply: NoteReply }>;
  remove: (noteId: string, replyId: string) => Promise<unknown>;
  onChange: (replies: NoteReply[]) => void;
  /** Called when the reply box opens. */
  onOpen?: () => void;
}

const ROLE_LABEL: Record<string, string> = { athlete: 'Player', guardian: 'Parent' };

function authorLabel(reply: NoteReply): string {
  if (reply.is_mine) return 'You';
  const role = ROLE_LABEL[reply.author_role];
  return role ? `${reply.author_name} · ${role}` : reply.author_name;
}

function stamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  return sameDay
    ? d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

const NoteReplyThread: React.FC<NoteReplyThreadProps> = ({
  noteId, replies, canReply, maxLength = DEFAULT_REPLY_MAX_LENGTH, send, remove, onChange, onOpen,
}) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const replyButtonRef = useRef<HTMLButtonElement>(null);
  const boxRef = useRef<HTMLTextAreaElement>(null);
  const returnFocus = useRef(false);

  useEffect(() => {
    if (open) {
      boxRef.current?.focus();
    } else if (returnFocus.current) {
      returnFocus.current = false;
      replyButtonRef.current?.focus();
    }
  }, [open]);

  const trimmed = text.trim();
  const tooLong = trimmed.length > maxLength;

  const openBox = () => {
    setOpen(true);
    setError(null);
    onOpen?.();
  };

  const closeBox = () => {
    returnFocus.current = true;
    setOpen(false);
    setText('');
    setError(null);
  };

  const submit = async () => {
    if (!trimmed || tooLong || sending) return;
    setSending(true);
    setError(null);
    try {
      const res = await send(noteId, trimmed);
      onChange([...replies, res.reply]);
      closeBox();
    } catch (err: any) {
      setError(err?.message || 'Your reply did not send. Try again.');
    } finally {
      setSending(false);
    }
  };

  const confirmDelete = async () => {
    if (!confirmDeleteId) return;
    setDeleting(true);
    try {
      await remove(noteId, confirmDeleteId);
      onChange(replies.filter((r) => r.id !== confirmDeleteId));
      setConfirmDeleteId(null);
    } catch (err: any) {
      setError(err?.message || 'That did not delete. Try again.');
      setConfirmDeleteId(null);
    } finally {
      setDeleting(false);
    }
  };

  if (replies.length === 0 && !canReply) return null;

  return (
    <div style={{ marginTop: space[3], fontFamily: font.sans }}>
      {replies.length > 0 && (
        <div
          role="list"
          aria-label={`${replies.length} ${replies.length === 1 ? 'reply' : 'replies'}`}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: space[2],
            paddingLeft: space[3],
            borderLeft: '2px solid var(--aps-border)',
            marginBottom: space[2],
          }}
        >
          {replies.map((reply) => (
            <div
              key={reply.id}
              role="listitem"
              style={{ display: 'flex', flexDirection: 'column', alignItems: reply.is_mine ? 'flex-end' : 'flex-start' }}
            >
              <MessageBubble
                direction={reply.is_mine ? 'out' : 'in'}
                author={authorLabel(reply)}
                timestamp={stamp(reply.created_at)}
                style={{ maxWidth: '90%' }}
              >
                {reply.body}
              </MessageBubble>
              {reply.is_mine && (
                <button
                  type="button"
                  onClick={() => setConfirmDeleteId(reply.id)}
                  aria-label="Delete your reply"
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: `2px ${space[1]}px`,
                    minHeight: 24,
                    cursor: 'pointer',
                    fontFamily: font.sans,
                    fontSize: 11,
                    fontWeight: font.weight.semibold,
                    color: 'var(--aps-text-muted)',
                  }}
                >
                  Delete
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {canReply && !open && (
        <Button
          ref={replyButtonRef}
          type="button"
          variant="ghost"
          size="sm"
          leftIcon={<CornerDownRight size={14} aria-hidden />}
          onClick={openBox}
          style={{ minHeight: 36 }}
        >
          Reply
        </Button>
      )}

      {!canReply && replies.length > 0 && (
        <p style={{ margin: 0, fontFamily: font.sans, fontSize: 12, color: 'var(--aps-text-muted)' }}>
          Replies are closed.
        </p>
      )}

      {canReply && open && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: space[2] }}>
          <TextArea
            ref={boxRef}
            rows={2}
            aria-label="Your reply"
            placeholder="Write a reply"
            value={text}
            invalid={tooLong}
            disabled={sending}
            onChange={(e) => { setText(e.target.value); if (error) setError(null); }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                closeBox();
              } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                submit();
              }
            }}
            style={{ minHeight: 64, fontSize: font.size.label }}
          />
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: space[2] }}>
            <Button type="button" size="sm" loading={sending} disabled={!trimmed || tooLong || sending} onClick={submit}>
              Send
            </Button>
            <Button type="button" variant="secondary" size="sm" disabled={sending} onClick={closeBox}>
              Cancel
            </Button>
            {trimmed.length > maxLength * 0.9 && (
              <span
                aria-live="polite"
                style={{ marginLeft: 'auto', fontSize: font.size.caption, color: tooLong ? 'var(--aps-danger)' : 'var(--aps-text-muted)' }}
              >
                {trimmed.length}/{maxLength}
              </span>
            )}
          </div>
        </div>
      )}

      {error && (
        <p
          role="alert"
          style={{
            display: 'flex',
            gap: space[2],
            margin: `${space[2]}px 0 0`,
            fontSize: font.size.labelSm,
            fontWeight: font.weight.semibold,
            color: 'var(--aps-danger)',
          }}
        >
          <AlertCircle size={16} aria-hidden style={{ flexShrink: 0, marginTop: 1, color: 'var(--aps-danger)' }} />
          {error}
        </p>
      )}

      <ConfirmDialog
        isOpen={confirmDeleteId !== null}
        onClose={() => { if (!deleting) setConfirmDeleteId(null); }}
        onConfirm={confirmDelete}
        title="Delete reply"
        message="Delete your reply? This can't be undone."
        confirmText="Delete"
        variant="danger"
        isLoading={deleting}
      />
    </div>
  );
};

export default NoteReplyThread;
